import React, { useState, useEffect } from 'react'
import { ChevronDown, Plus, Edit2, Trash2, Building2, ExternalLink, Star } from 'lucide-react'
import { apiClient } from '@/services/api'
import { useTemplateForm } from '@/hooks/useTemplateForm'

interface Supplier {
  id: string
  name: string
  description?: string
  contactPerson?: string
  email?: string
  phone?: string
  address?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
  website?: string
  notes?: string
  specialties?: string[]
  rating?: number
  isPreferred?: boolean
  paymentTerms?: {
    method?: string
    terms?: string
    creditLimit?: number
  }
  shippingInfo?: {
    methods?: string[]
    freeShippingThreshold?: number
    averageDeliveryDays?: number
  }
}

interface SupplierManagerProps {
  selectedSupplierId?: string
  onSupplierChange: (supplierId: string) => void
}

export const SupplierManager: React.FC<SupplierManagerProps> = ({
  selectedSupplierId,
  onSupplierChange
}) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(false)
  const [showManagement, setShowManagement] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null)
  const [newSupplier, setNewSupplier] = useState<Partial<Supplier>>({
    name: '',
    description: '',
    website: '',
    specialties: [],
    rating: 5,
    isPreferred: false
  })

  // Get current template information
  const { currentTemplate } = useTemplateForm()

  // Template-aware helper functions
  const getTemplatePlaceholder = (businessType: string): string => {
    switch (businessType) {
      case '3d_printing':
        return 'e.g., Hatchbox 3D, Prusament, eSUN'
      case 'jewelry':
        return 'e.g., Fire Mountain Gems, Rio Grande'
      case 'woodworking':
        return 'e.g., Rockler, Woodcraft'
      case 'electronics':
        return 'e.g., Adafruit, SparkFun, Digi-Key'
      case 'automotive':
        return 'e.g., AutoZone, O\'Reilly Auto Parts'
      default:
        return 'e.g., Supplier Name'
    }
  }

  const fetchSuppliers = async () => {
    try {
      setLoading(true)
      
      // Use template-aware API call
      let url = '/suppliers'
      if (currentTemplate?.businessType) {
        url += `?businessType=${encodeURIComponent(currentTemplate.businessType)}`
      }
      
      console.log('🔍 Fetching suppliers with URL:', url)
      const response = await apiClient.get(url)
      const suppliers = response || []
      
      console.log('🔍 Suppliers response:', suppliers)
      
      // Sort by preference then name
      const sortedSuppliers = suppliers.sort((a: Supplier, b: Supplier) => {
        if (a.isPreferred && !b.isPreferred) return -1
        if (!a.isPreferred && b.isPreferred) return 1
        return a.name.localeCompare(b.name)
      })
      
      setSuppliers(sortedSuppliers)
    } catch (error) {
      console.error('Error fetching suppliers:', error)
    } finally {
      setLoading(false)
    }
  }


  const createSupplier = async () => {
    if (!newSupplier.name?.trim()) return

    try {
      setLoading(true)
      
      // Include current template's businessType when creating supplier
      const supplierData = {
        ...newSupplier,
        businessType: currentTemplate?.businessType
      }
      
      const response = await apiClient.post('/suppliers', supplierData)
      setSuppliers([...suppliers, response])
      setNewSupplier({ name: '', description: '', website: '', specialties: [], rating: 5, isPreferred: false })
      setShowAddForm(false)
    } catch (error) {
      console.error('Error creating supplier:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateSupplier = async (supplier: Supplier) => {
    try {
      setLoading(true)
      const response = await apiClient.patch(`/suppliers/${supplier.id}`, supplier)
      setSuppliers(suppliers.map(s => s.id === supplier.id ? response : s))
      setEditingSupplier(null)
    } catch (error) {
      console.error('Error updating supplier:', error)
    } finally {
      setLoading(false)
    }
  }

  const deleteSupplier = async (supplierId: string) => {
    if (!confirm('Are you sure you want to delete this supplier?')) return

    try {
      setLoading(true)
      await apiClient.delete(`/suppliers/${supplierId}`)
      setSuppliers(suppliers.filter(s => s.id !== supplierId))
      if (selectedSupplierId === supplierId) {
        onSupplierChange('')
      }
    } catch (error) {
      console.error('Error deleting supplier:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSuppliers()
  }, [])

  // Re-fetch suppliers when template changes
  useEffect(() => {
    if (currentTemplate) {
      fetchSuppliers()
    }
  }, [currentTemplate?.id])

  const selectedSupplier = suppliers.find(s => s.id === selectedSupplierId)

  const renderStars = (rating?: number) => {
    if (!rating) return null
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            className={`w-3 h-3 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Supplier Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Supplier (Optional)
        </label>
        <div className="relative">
          <select
            value={selectedSupplierId || ''}
            onChange={(e) => onSupplierChange(e.target.value)}
            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white appearance-none"
          >
            <option value="">
              {currentTemplate
                ? `Select a supplier for ${currentTemplate.name}...`
                : 'Select a supplier...'
              }
            </option>
            {suppliers.map(supplier => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
                {supplier.isPreferred && ' ⭐'}
                {supplier.rating && ` (${supplier.rating}/5)`}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>

        {/* Selected Supplier Info */}
        {selectedSupplier && (
          <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-blue-900 dark:text-blue-100">
                    {selectedSupplier.name}
                  </span>
                  {selectedSupplier.isPreferred && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                      Preferred
                    </span>
                  )}
                  {renderStars(selectedSupplier.rating)}
                </div>
                {selectedSupplier.description && (
                  <p className="text-sm text-blue-700 dark:text-blue-200 mt-1">
                    {selectedSupplier.description}
                  </p>
                )}
                {selectedSupplier.specialties && selectedSupplier.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedSupplier.specialties.map((specialty, index) => (
                      <span
                        key={index}
                        className="text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {selectedSupplier.website && (
                <a
                  href={selectedSupplier.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Template Information */}
        {currentTemplate && (
          <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              <strong>Template:</strong> {currentTemplate.name} - Showing suppliers for {currentTemplate.industry} business type.
            </p>
          </div>
        )}
      </div>

      {/* Management Buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setShowManagement(!showManagement)}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          <Building2 className="w-4 h-4" />
          Manage Suppliers
        </button>

      </div>

      {/* Management Panel */}
      {showManagement && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Supplier Management
            </h3>
            <button
              type="button"
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-md hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Supplier
            </button>
          </div>

          {/* Add Supplier Form */}
          {showAddForm && (
            <div className="mb-4 p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                Add New Supplier
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Supplier Name *
                  </label>
                  <input
                    type="text"
                    value={newSupplier.name || ''}
                    onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                    placeholder={currentTemplate ? getTemplatePlaceholder(currentTemplate.businessType) : 'e.g., Supplier Name'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    value={newSupplier.website || ''}
                    onChange={(e) => setNewSupplier({ ...newSupplier, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Rating
                  </label>
                  <select
                    value={newSupplier.rating || 5}
                    onChange={(e) => setNewSupplier({ ...newSupplier, rating: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                  >
                    <option value={1}>⭐ (1/5)</option>
                    <option value={2}>⭐⭐ (2/5)</option>
                    <option value={3}>⭐⭐⭐ (3/5)</option>
                    <option value={4}>⭐⭐⭐⭐ (4/5)</option>
                    <option value={5}>⭐⭐⭐⭐⭐ (5/5)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={newSupplier.description || ''}
                    onChange={(e) => setNewSupplier({ ...newSupplier, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                    placeholder="Brief description of the supplier"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={createSupplier}
                  disabled={!newSupplier.name?.trim() || loading}
                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Adding...' : 'Add Supplier'}
                </button>
              </div>
            </div>
          )}

          {/* Suppliers List */}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {suppliers.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                No suppliers found. Add some suppliers to get started.
              </p>
            ) : (
              suppliers.map(supplier => (
                <div
                  key={supplier.id}
                  className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex-1">
                    {editingSupplier?.id === supplier.id ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <input
                          type="text"
                          value={editingSupplier?.name || ''}
                          onChange={(e) => editingSupplier && setEditingSupplier({ ...editingSupplier, name: e.target.value })}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                          placeholder="Supplier name"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && editingSupplier) updateSupplier(editingSupplier)
                            if (e.key === 'Escape') setEditingSupplier(null)
                          }}
                        />
                        <input
                          type="text"
                          value={editingSupplier?.description || ''}
                          onChange={(e) => editingSupplier && setEditingSupplier({ ...editingSupplier, description: e.target.value })}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                          placeholder="Description"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && editingSupplier) updateSupplier(editingSupplier)
                            if (e.key === 'Escape') setEditingSupplier(null)
                          }}
                        />
                        <select
                          value={editingSupplier?.rating || 5}
                          onChange={(e) => editingSupplier && setEditingSupplier({ ...editingSupplier, rating: parseInt(e.target.value) })}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:text-white"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && editingSupplier) updateSupplier(editingSupplier)
                            if (e.key === 'Escape') setEditingSupplier(null)
                          }}
                        >
                          <option value={1}>⭐ (1/5)</option>
                          <option value={2}>⭐⭐ (2/5)</option>
                          <option value={3}>⭐⭐⭐ (3/5)</option>
                          <option value={4}>⭐⭐⭐⭐ (4/5)</option>
                          <option value={5}>⭐⭐⭐⭐⭐ (5/5)</option>
                        </select>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {supplier.name}
                          </span>
                          {supplier.isPreferred && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                              Preferred
                            </span>
                          )}
                          {renderStars(supplier.rating)}
                        </div>
                        {supplier.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {supplier.description}
                          </p>
                        )}
                        {supplier.website && (
                          <a
                            href={supplier.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {supplier.website}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {editingSupplier?.id === supplier.id ? (
                      <>
                        <button
                          type="button"
                          onClick={() => updateSupplier(editingSupplier)}
                          className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                          title="Save"
                        >
                          ✓
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingSupplier(null)}
                          className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setEditingSupplier(supplier)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteSupplier(supplier.id)}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}